import React, { useState, useEffect } from "react";
import * as Chakra from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import InputC from "../../theme/InputC";
import ButtonC from "../../theme/ButtonC";
import SelectC from "../../theme/SelectC";
import { INDIAN_STATES } from "../../schemas/Address";

// -------------------------------
// ZOD VALIDATION SCHEMA (INDIA)
// -------------------------------
const AddressSchemaIN = z.object({
  name: z.string().min(3, "Full Name must have at least 3 characters"),

  address_line1: z.string().min(5, "Address Line 1 must be at least 5 characters"),

  address_line2: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine((v) => v === "" || v.length >= 3, {
      message: "Address Line 2 must be at least 3 characters",
    }),

  postal_code: z
    .string()
    .regex(/^[1-9][0-9]{5}$/, "Enter a valid 6-digit PIN code"),

  city: z.string().min(2, "City cannot be empty"),

  state: z.string().min(2, "State cannot be empty"),

  phone: z
    .string()
    .regex(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number"),

  email: z
    .string()
    .email("Enter a valid email")
    .optional()
    .or(z.literal("")),

  country: z.string().default("IN"),
});

// -------------------------------
// DEBOUNCE HOOK
// -------------------------------
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

// -------------------------------
// MAIN FORM COMPONENT
// -------------------------------
export default function AddressForm({ onSubmit, onClose, cssClass, initialData }) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting, isValid },
  } = useForm({
    resolver: zodResolver(AddressSchemaIN),
    mode: "onChange",     
    defaultValues: {
      country: "IN",
      ...initialData,
    },
  });

  console.log(errors);
  const [loadingPin, setLoadingPin] = useState(false);

  const postalCode = watch("postal_code");
  const debouncedPostalCode = useDebounce(postalCode, 300);


  useEffect(() => {
    const fetchLocation = async () => {
      if (/^[1-9][0-9]{5}$/.test(debouncedPostalCode)) {
        setLoadingPin(true);

        try {
          const res = await fetch(
            `https://api.postalpincode.in/pincode/${debouncedPostalCode}`
          );
          const data = await res.json();
          const post = data?.[0]?.PostOffice?.[0];

          if (post) {
            setValue("city", post.Block || "", { shouldValidate: true });
            setValue("state", post.State || "", { shouldValidate: true });

            await trigger(["city", "state"]);
          } else {
            setValue("city", "", { shouldValidate: true });
            setValue("state", "", { shouldValidate: true });
            await trigger(["city", "state"]);
          }
        } catch (err) {
          console.error("PIN lookup failed", err);
        } finally {
          setLoadingPin(false);
        }
      }
    };

    fetchLocation();
  }, [debouncedPostalCode, setValue, trigger]);

  return (
    <div className={cssClass}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ width: "100%" }}>
        <Chakra.SimpleGrid columns={{ base: 1, mdTo2xl: 2 }} gapX={6} gapY={3}>

          <InputC
            isError={!!errors.name}
            errorMsg={errors.name?.message}
            label="Full Name"
            borderColor={errors.name ? "red.500" : "#cfdddfff"}
            {...register("name")}
          />


          <InputC
            isError={!!errors.address_line1}
            errorMsg={errors.address_line1?.message}
            label="Address Line 1"
            borderColor={errors.address_line1 ? "red.500" : "#cfdddfff"}
            {...register("address_line1")}
          />


          <InputC
            isError={!!errors.address_line2}
            errorMsg={errors.address_line2?.message}
            label="Address Line 2"
            borderColor={errors.address_line2 ? "red.500" : "#cfdddfff"}
            {...register("address_line2")}
          />

   
          <InputC
            isError={!!errors.postal_code}
            errorMsg={errors.postal_code?.message}
            label="PIN Code"
            maxLength={6}
            borderColor={errors.postal_code ? "red.500" : "#cfdddfff"}
            {...register("postal_code")}
          />

          <InputC
            isError={!!errors.city}
            errorMsg={errors.city?.message}
            loadingPin={loadingPin}
            label="City"
            borderColor={errors.city ? "red.500" : "#cfdddfff"}
            {...register("city")}
          />


          <SelectC
            isError={!!errors.state}
            errorMsg={errors.state?.message}
            label="State / UT"
            borderColor={errors.state ? "red.500" : "#cfdddfff"}
            {...register("state")}
            items={INDIAN_STATES.map((s) => ({ label: s, value: s }))}
            placeholder="Select State"
          />


          <InputC
            isError={!!errors.phone}
            errorMsg={errors.phone?.message}
            label="Phone Number"
            prefix="+91"
            placeholder="XXXXXXXXXX"
            borderColor={errors.phone ? "red.500" : "#cfdddfff"}
            {...register("phone")}
          />


          <InputC
            isError={!!errors.email}
            errorMsg={errors.email?.message}
            label="Email"
            borderColor={errors.email ? "red.500" : "#cfdddfff"}
            {...register("email")}
          />
        </Chakra.SimpleGrid>


        <Chakra.SimpleGrid mt="4" justifyItems="center">

          <Chakra.Flex direction="row" gapX={4}>
            <ButtonC
              label="Save Address"
              type="submit"
              isLoading={isSubmitting}
              disabled={!isValid}     
            />

            <ButtonC label="Cancel" type="button" onClick={onClose} />
          </Chakra.Flex>

        </Chakra.SimpleGrid>
      </form>
    </div>
  );
}
